import { Controller } from "@hotwired/stimulus"
import { intlLocale } from "../helpers/locale"

// Drives two screens, both mounted with `data-controller="expense-filter"`:
//
//   1. expenses/index.html.erb — only the sticky search bar. Most targets
//      below won't exist there; the controller no-ops via `has*Target` guards.
//
//   2. expenses/filter.html.erb — the filter form. Presented as a Hotwire
//      Native modal (path-config rule `.*/expenses/filter`). Apply submits
//      the form GET to the index URL with filter params; Hotwire Native
//      dismisses the modal automatically because the destination is a
//      default-context route.
export default class extends Controller {
  static targets = [
    "searchInput",
    "categoryChip", "frequencyChip", "reminderChip", "typeChip",
    "frequencySummary", "reminderSummary",
    "categoryLabel", "categoryTriggerLabel", "categoryTriggerIcon",
    "pickerCategorySelectedTile", "pickerCategorySelectedLabel",
    "categoryPickerOverlay",
    "dateFrom", "dateTo",
    "dateFromDisplay", "dateToDisplay",
    "filterForm", "filterSheet", "filterBackdrop", "barButton", "clearSearchBtn",
    "hiddenCategory", "hiddenFrequency", "hiddenReminder", "hiddenType", "hiddenDateFrom", "hiddenDateTo", "hiddenSearch",
    "applyBtn", "applySpinner", "applyLabel"
  ]

  connect() {
    this._filterOpen = false
    this._handleEscape = (e) => { if (e.key === "Escape") this._handleEscapeKey() }
    document.addEventListener("keydown", this._handleEscape)

    // iOS WKWebView doesn't reliably fire change events on date inputs.
    // Poll to catch date picker changes that events miss.
    this._lastDateFrom = this.hasDateFromTarget ? this.dateFromTarget.value : null
    this._lastDateTo = this.hasDateToTarget ? this.dateToTarget.value : null
    this._datePoller = setInterval(() => this._pollDateChanges(), 300)
  }

  disconnect() {
    document.removeEventListener("keydown", this._handleEscape)
    if (this._datePoller) {
      clearInterval(this._datePoller)
      this._datePoller = null
    }
  }

  // ── Filter Bottom Sheet (in-page) ──
  // The native filter bar button (bridge--button) and the web fallback button
  // fire openFilterSheet on click. The sheet slides up from the bottom over a
  // dark backdrop, mirroring the voice FAB sheet (voice_modal_controller).
  // Opening only animates an in-page element, so the native bar button is never
  // torn down / re-registered (which made it un-clickable after the first apply
  // when the filter was a native modal).

  openFilterSheet(event) {
    if (event) event.preventDefault()
    if (!this.hasFilterSheetTarget) return
    this._filterOpen = true

    // Backdrop — fade in
    if (this.hasFilterBackdropTarget) {
      this.filterBackdropTarget.style.opacity = ""
      this.filterBackdropTarget.classList.remove("opacity-0", "pointer-events-none")
    }

    // Lock the page underneath so it can't scroll while the sheet is up
    document.body.style.overflow = "hidden"

    // Sheet — iOS spring (overshoot bounce, smooth settle)
    this.filterSheetTarget.style.transition = "transform 460ms cubic-bezier(0.2, 0.8, 0.25, 1)"
    this.filterSheetTarget.offsetHeight // force reflow so transition fires from current position
    this.filterSheetTarget.style.transform = "translateY(0)"

    // Hide the native bottom nav while the sheet is up
    this._bottomNavBridge?.hide()

    // Dim the native nav bar (title bar) behind the sheet — same as the FAB
    // voice modal. The web backdrop can't cover the native bar, so we ask the
    // filter bar-button bridge to drop a native dimming overlay over it.
    this._filterButtonBridge?.dim()
  }

  closeFilterSheet() {
    if (!this.hasFilterSheetTarget) return
    this._filterOpen = false

    // pointer-events-none MUST be added synchronously — if a Turbo morph/refresh
    // happens during the animation, the old backdrop could persist and block all
    // touches on iOS. (Same guard as confirm-sheet / voice-modal.)
    if (this.hasFilterBackdropTarget) {
      this.filterBackdropTarget.style.opacity = ""
      this.filterBackdropTarget.classList.add("opacity-0", "pointer-events-none")
    }

    // Restore page scrolling
    document.body.style.overflow = ""

    // Sheet — iOS dismiss (smooth deceleration), back off-screen
    this.filterSheetTarget.style.transition = "transform 340ms cubic-bezier(0.4, 0, 0.2, 1)"
    this.filterSheetTarget.style.transform = "translateY(115%)"

    this._bottomNavBridge?.show()
    this._filterButtonBridge?.undim()
  }

  // Backdrop tap to close.
  backdropClose(event) {
    if (event.target === this.filterBackdropTarget) this.closeFilterSheet()
  }

  // ── Swipe-to-dismiss (iOS-style drag from anywhere on the sheet) ──
  // Selector for elements that consume the touch instead of starting a drag
  // (inputs/buttons/links/labels + anything opted-in via data-no-drag).
  static DRAG_IGNORE_SELECTOR = "input, textarea, select, button, a, label, [contenteditable], [data-no-drag]"

  // A sheet is dragged by its handle, not by its contents. Binding the whole
  // surface meant a touch anywhere — on a mascot, on a paragraph, on the padding
  // between two rows — slid the sheet and moved the dashboard behind it, which
  // reads as the page coming loose rather than as a gesture.
  static DRAG_HANDLE_SELECTOR = "[data-drag-handle]"

  dragStart(event) {
    if (event.target.closest(this.constructor.DRAG_IGNORE_SELECTOR)) {
      this._isDragging = false
      return
    }
    if (!event.target.closest(this.constructor.DRAG_HANDLE_SELECTOR)) {
      this._isDragging = false
      return
    }
    const touch = event.touches[0]
    this._dragStartY = touch.clientY
    this._dragCurrentY = touch.clientY
    this._dragStartX = touch.clientX
    this._dragCommitted = false
    this._isDragging = true
    this.filterSheetTarget.style.transition = "none"
  }

  dragMove(event) {
    if (!this._isDragging) return

    const touch = event.touches[0]
    this._dragCurrentY = touch.clientY
    const deltaY = this._dragCurrentY - this._dragStartY
    const deltaX = touch.clientX - this._dragStartX

    // Commit to a vertical drag only once a small threshold is crossed.
    // Horizontal-dominant movement releases the gesture (text selection etc).
    if (!this._dragCommitted) {
      if (Math.abs(deltaY) < 6 && Math.abs(deltaX) < 6) return
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        this._isDragging = false
        return
      }
      this._dragCommitted = true
    }

    if (deltaY <= 0) {
      this.filterSheetTarget.style.transform = "translateY(0)"
      return
    }

    // Stop the page underneath from scrolling while we drag the sheet down.
    if (event.cancelable) event.preventDefault()

    this.filterSheetTarget.style.transform = `translateY(${deltaY}px)`
    if (this.hasFilterBackdropTarget) {
      const progress = Math.min(deltaY / (window.innerHeight * 0.3), 1)
      this.filterBackdropTarget.style.opacity = 1 - progress * 0.8
    }
  }

  dragEnd() {
    if (!this._isDragging) return
    const wasCommitted = this._dragCommitted
    this._isDragging = false
    this._dragCommitted = false

    if (!wasCommitted) {
      // Was a tap or horizontal swipe — snap back to the open position.
      this.filterSheetTarget.style.transition = ""
      this.filterSheetTarget.style.transform = "translateY(0)"
      if (this.hasFilterBackdropTarget) this.filterBackdropTarget.style.opacity = ""
      return
    }

    const deltaY = this._dragCurrentY - this._dragStartY
    const velocity = deltaY / (window.innerHeight || 1)

    if (deltaY > 100 || velocity > 0.2) {
      this.closeFilterSheet()
    } else {
      this.filterSheetTarget.style.transition = "transform 300ms cubic-bezier(0.2, 0.8, 0.25, 1)"
      this.filterSheetTarget.style.transform = "translateY(0)"
      if (this.hasFilterBackdropTarget) this.filterBackdropTarget.style.opacity = ""
    }
  }

  // Returns the bridge--bottom-nav controller if connected (native iOS app).
  get _bottomNavBridge() {
    const el = document.querySelector("[data-controller~='bridge--bottom-nav']")
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "bridge--bottom-nav")
  }

  // Returns the bridge--button controller for the native filter bar button.
  // Used to dim/undim the native nav bar behind the sheet (native iOS app).
  get _filterButtonBridge() {
    if (!this.hasBarButtonTarget) return null
    return this.application.getControllerForElementAndIdentifier(this.barButtonTarget, "bridge--button")
  }

  // ── Category Picker Overlay (full-screen, in-modal) ──

  openCategoryPicker() {
    if (this.hasCategoryPickerOverlayTarget) {
      this.categoryPickerOverlayTarget.classList.remove("hidden")
      this._resetCategoryFilter()
    }
  }

  closeCategoryPicker() {
    if (this.hasCategoryPickerOverlayTarget) {
      this.categoryPickerOverlayTarget.classList.add("hidden")
    }
    this._updateCategoryLabel()
    this._updateCategoryTrigger()
  }

  _resetCategoryFilter() {
    if (!this.hasCategoryPickerOverlayTarget) return
    const input = this.categoryPickerOverlayTarget.querySelector("[data-category-search]")
    if (input) input.value = ""
    this.categoryPickerOverlayTarget.querySelectorAll("[data-cat]").forEach(el => el.classList.remove("hidden"))
    this.categoryPickerOverlayTarget.querySelectorAll(".grid").forEach(grid => {
      grid.classList.remove("hidden")
      const header = grid.previousElementSibling
      if (header) header.classList.remove("hidden")
    })
  }

  filterCategories(e) {
    if (!this.hasCategoryPickerOverlayTarget) return
    const q = (e.target.value || "").trim().toLowerCase()
    const overlay = this.categoryPickerOverlayTarget
    overlay.querySelectorAll("[data-cat]").forEach(btn => {
      const label = (btn.dataset.label || btn.textContent || "").toLowerCase()
      btn.classList.toggle("hidden", q.length > 0 && !label.includes(q))
    })
    overlay.querySelectorAll(".grid").forEach(grid => {
      const anyVisible = Array.from(grid.children).some(c => !c.classList.contains("hidden"))
      grid.classList.toggle("hidden", !anyVisible)
      const header = grid.previousElementSibling
      if (header) header.classList.toggle("hidden", !anyVisible)
    })
  }

  toggleCategoryItem(event) {
    const btn = event.currentTarget
    const wasActive = btn.dataset.active === "true"
    btn.dataset.active = wasActive ? "false" : "true"

    btn.classList.toggle("ring-2", !wasActive)
    btn.classList.toggle("ring-indigo-400", !wasActive)
    btn.classList.toggle("bg-indigo-50/30", !wasActive)
    btn.classList.toggle("dark:bg-indigo-900/20", !wasActive)

    this._updatePickerHero()
  }

  clearCategories() {
    this.categoryChipTargets.forEach(c => {
      c.dataset.active = "false"
      c.classList.remove("ring-2", "ring-indigo-400", "bg-indigo-50/30", "dark:bg-indigo-900/20")
    })
    this._updateCategoryLabel()
    this._updatePickerHero()
  }

  // ── Frequency segmented (single select, "" = Any) ──

  toggleFrequency(event) {
    const chip = event.currentTarget
    this.frequencyChipTargets.forEach(c => {
      c.dataset.active = "false"
      this._styleSegmentedChip(c, false)
    })
    chip.dataset.active = "true"
    this._styleSegmentedChip(chip, true)
    if (this.hasFrequencySummaryTarget) {
      this.frequencySummaryTarget.textContent = chip.dataset.label || ""
    }
  }

  // ── Reminder segmented (single select, "" = Any) ──

  toggleReminder(event) {
    const chip = event.currentTarget
    this.reminderChipTargets.forEach(c => {
      c.dataset.active = "false"
      this._styleSegmentedChip(c, false)
    })
    chip.dataset.active = "true"
    this._styleSegmentedChip(chip, true)
    if (this.hasReminderSummaryTarget) {
      this.reminderSummaryTarget.textContent = chip.dataset.label || ""
    }
  }

  // ── Type segmented (single select, "" = Any) ──

  toggleType(event) {
    const chip = event.currentTarget
    this.typeChipTargets.forEach(c => {
      c.dataset.active = "false"
      this._styleSegmentedChip(c, false)
    })
    chip.dataset.active = "true"
    this._styleSegmentedChip(chip, true)
  }

  // ── Apply filters ──
  // The form targets the <turbo-frame id="expenses_frame"> that wraps the list,
  // so submitting updates ONLY the list — the page shell and the native filter
  // bar button never re-render, so the bar button stays registered and can
  // reopen the overlay. Then we just hide the overlay; the frame loads behind.
  apply() {
    if (!this.hasFilterFormTarget) return
    this._syncHiddenInputs()
    this._setBadge(this._activeFilterCount())
    this.filterFormTarget.requestSubmit()
    this.closeFilterSheet()
  }

  // Reset every filter: clears the in-overlay UI (chips, dates), zeroes the
  // native bar-button badge, and closes the overlay. The Reset / Clear-all
  // links also navigate the expenses_frame to the unfiltered list. Because the
  // overlay lives outside that frame it isn't re-rendered, so we clear it here.
  resetAll(event) {
    this._keepLiveSearch(event?.currentTarget)

    this.categoryChipTargets.forEach(c => {
      c.dataset.active = "false"
      c.classList.remove("ring-2", "ring-indigo-400", "bg-indigo-50/30", "dark:bg-indigo-900/20")
    })
    this._resetSegment(this.frequencyChipTargets)
    this._resetSegment(this.reminderChipTargets)
    this._resetSegment(this.typeChipTargets)
    if (this.hasDateFromTarget) { this.dateFromTarget.value = ""; this._lastDateFrom = "" }
    if (this.hasDateToTarget) { this.dateToTarget.value = ""; this._lastDateTo = "" }
    this._syncDateDisplays()
    this._updateCategoryTrigger()
    this._setBadge(0)
    this.closeFilterSheet()
  }

  // One chip's × on the list: drop that single filter. The link already
  // points at the list without it (built in expenses/_expense_list from the
  // live query); this keeps the sheet — outside the frame, never re-rendered —
  // and the native badge in step, the way resetAll does for Clear all. Both
  // layers are updated: the chips (what the sheet shows) and the hidden
  // inputs (what Apply and the search form send).
  removeFilter(event) {
    const link = event.currentTarget
    const { filterKey: key, filterValue: value } = link.dataset
    this._keepLiveSearch(link)

    switch (key) {
      case "category":
        this.categoryChipTargets
          .filter(c => (c.dataset.value || c.dataset.cat) === value)
          .forEach(c => {
            c.dataset.active = "false"
            c.classList.remove("ring-2", "ring-indigo-400", "bg-indigo-50/30", "dark:bg-indigo-900/20")
          })
        this.hiddenCategoryTargets.filter(el => el.value === value).forEach(el => el.remove())
        this._updateCategoryTrigger()
        this._updateCategoryLabel()
        break
      case "frequency":
        this._resetSegment(this.frequencyChipTargets)
        if (this.hasHiddenFrequencyTarget) this.hiddenFrequencyTarget.value = ""
        break
      case "reminder":
        this._resetSegment(this.reminderChipTargets)
        if (this.hasHiddenReminderTarget) this.hiddenReminderTarget.value = ""
        break
      case "expense_type":
        this._resetSegment(this.typeChipTargets)
        if (this.hasHiddenTypeTarget) this.hiddenTypeTarget.value = ""
        break
      case "date_from":
        if (this.hasDateFromTarget) { this.dateFromTarget.value = ""; this._lastDateFrom = "" }
        if (this.hasHiddenDateFromTarget) this.hiddenDateFromTarget.value = ""
        this._syncDateDisplays()
        break
      case "date_to":
        if (this.hasDateToTarget) { this.dateToTarget.value = ""; this._lastDateTo = "" }
        if (this.hasHiddenDateToTarget) this.hiddenDateToTarget.value = ""
        this._syncDateDisplays()
        break
    }

    this._setBadge(this._activeFilterCount())
  }

  // A chip link's href carries the page-load q; swap in what is in the search
  // bar right now so dropping a filter does not also drop the search.
  _keepLiveSearch(link) {
    if (!link?.href || !this.hasSearchInputTarget) return
    const url = new URL(link.href, window.location.href)
    const q = this.searchInputTarget.value.trim()
    if (q) url.searchParams.set("q", q); else url.searchParams.delete("q")
    link.href = url.toString()
  }

  _resetSegment(chips) {
    chips.forEach(c => {
      const isAny = (c.dataset.value || "") === ""
      c.dataset.active = isAny ? "true" : "false"
      this._styleSegmentedChip(c, isAny)
    })
  }

  _activeFilterCount() {
    let n = 0
    if (this.categoryChipTargets.some(c => c.dataset.active === "true")) n++
    if (this.frequencyChipTargets.some(c => c.dataset.active === "true" && c.dataset.value)) n++
    if (this.reminderChipTargets.some(c => c.dataset.active === "true" && c.dataset.value)) n++
    if (this.typeChipTargets.some(c => c.dataset.active === "true" && c.dataset.value)) n++
    if (this.hasDateFromTarget && this.dateFromTarget.value) n++
    if (this.hasDateToTarget && this.dateToTarget.value) n++
    return n
  }

  _setBadge(count) {
    if (this.hasBarButtonTarget) {
      this.barButtonTarget.setAttribute("data-bridge--button-badge-count-value", count)
    }
  }

  _setApplyLoading(loading) {
    if (this.hasApplyBtnTarget) this.applyBtnTarget.disabled = loading
    if (this.hasApplySpinnerTarget) this.applySpinnerTarget.classList.toggle("hidden", !loading)
  }

  // ── Search submit (lives on the index page) ──

  search(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this._submitSearchForm()
    }
  }

  clearSearch() {
    if (!this.hasSearchInputTarget) return
    this.searchInputTarget.value = ""
    this._toggleClearBtn()
    this._submitSearchForm()
  }

  // ── Date display sync ──

  onDateChange() {
    this._syncDateDisplays()
  }

  _pollDateChanges() {
    let changed = false
    if (this.hasDateFromTarget && this.dateFromTarget.value !== this._lastDateFrom) {
      this._lastDateFrom = this.dateFromTarget.value
      changed = true
    }
    if (this.hasDateToTarget && this.dateToTarget.value !== this._lastDateTo) {
      this._lastDateTo = this.dateToTarget.value
      changed = true
    }
    if (changed) this._syncDateDisplays()
  }

  _syncDateDisplays() {
    if (this.hasDateFromDisplayTarget && this.hasDateFromTarget) {
      this._renderDateDisplay(this.dateFromDisplayTarget, this.dateFromTarget.value)
    }
    if (this.hasDateToDisplayTarget && this.hasDateToTarget) {
      this._renderDateDisplay(this.dateToDisplayTarget, this.dateToTarget.value)
    }
  }

  _renderDateDisplay(el, value) {
    const filled = !!value
    el.textContent = filled ? this._formatDate(value) : (el.dataset.emptyText || "Any date")
    el.classList.toggle("text-base-content", filled)
    el.classList.toggle("text-base-content/40", !filled)
  }

  _formatDate(isoString) {
    if (!isoString) return ""
    const date = new Date(isoString + "T00:00:00")
    // Short month: the two date fields share one row, so a long month name
    // truncates. Matches the server-rendered `format: :short` in the sheet.
    return date.toLocaleDateString(intlLocale(), {
      year: "numeric", month: "short", day: "numeric"
    })
  }

  // ── Private ──

  _handleEscapeKey() {
    if (this.hasCategoryPickerOverlayTarget && !this.categoryPickerOverlayTarget.classList.contains("hidden")) {
      this.closeCategoryPicker()
    } else if (this._filterOpen) {
      this.closeFilterSheet()
    }
  }

  _submitSearchForm() {
    if (!this.hasSearchInputTarget) return
    const searchForm = this.searchInputTarget.closest("form")
    if (!searchForm) return
    this._mirrorAppliedFilters(searchForm)
    searchForm.requestSubmit()
  }

  // Same staleness the other way round: the search form's hidden filter
  // fields are the page-load params, and Apply updates only the frame. Rebuild
  // them from the sheet's hidden inputs, which hold the last APPLIED state
  // (server-rendered, then rewritten by _syncHiddenInputs on Apply) — not the
  // chips, which may carry toggles the user never applied.
  _mirrorAppliedFilters(searchForm) {
    searchForm.querySelectorAll('input[type="hidden"]').forEach(el => el.remove())
    if (!this.hasFilterFormTarget) return

    this.filterFormTarget.querySelectorAll('input[type="hidden"]').forEach(src => {
      if (src.name === "q" || !src.value) return
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = src.name
      input.value = src.value
      searchForm.appendChild(input)
    })
  }

  _syncHiddenInputs() {
    this.hiddenCategoryTargets.forEach(el => el.remove())

    const cats = this.categoryChipTargets
      .filter(c => c.dataset.active === "true")
      .map(c => c.dataset.value || c.dataset.cat)

    cats.forEach(cat => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "category[]"
      input.value = cat
      input.dataset.expenseFilterTarget = "hiddenCategory"
      this.filterFormTarget.appendChild(input)
    })

    const freq = this.frequencyChipTargets.find(c => c.dataset.active === "true")
    if (this.hasHiddenFrequencyTarget) {
      this.hiddenFrequencyTarget.value = freq ? freq.dataset.value : ""
    }

    const reminder = this.reminderChipTargets.find(c => c.dataset.active === "true")
    if (this.hasHiddenReminderTarget) {
      this.hiddenReminderTarget.value = reminder ? reminder.dataset.value : ""
    }

    const type = this.typeChipTargets.find(c => c.dataset.active === "true")
    if (this.hasHiddenTypeTarget) {
      this.hiddenTypeTarget.value = type ? type.dataset.value : ""
    }

    if (this.hasHiddenDateFromTarget && this.hasDateFromTarget) {
      this.hiddenDateFromTarget.value = this.dateFromTarget.value
    }
    if (this.hasHiddenDateToTarget && this.hasDateToTarget) {
      this.hiddenDateToTarget.value = this.dateToTarget.value
    }

    // The sheet's hidden q was rendered with the page, and the search bar
    // submits into the frame without re-rendering the sheet — so without
    // this, applying a filter sends the search text from page load (usually
    // nothing) and the search silently disappears.
    if (this.hasHiddenSearchTarget && this.hasSearchInputTarget) {
      this.hiddenSearchTarget.value = this.searchInputTarget.value.trim()
    }
  }

  _updateCategoryLabel() {
    if (!this.hasCategoryLabelTarget) return
    const selected = this.categoryChipTargets
      .filter(c => c.dataset.active === "true")
      .map(c => c.dataset.label || c.dataset.value)

    const allText = this.categoryLabelTarget.dataset.allText || "All"
    this.categoryLabelTarget.textContent = selected.length > 0
      ? selected.join(", ")
      : allText
  }

  _updateCategoryTrigger() {
    if (!this.hasCategoryTriggerLabelTarget) return
    const labelEl = this.categoryTriggerLabelTarget
    labelEl.textContent = this._computeCategorySummary(labelEl)
  }

  _updatePickerHero() {
    if (!this.hasPickerCategorySelectedLabelTarget) return
    const labelEl = this.pickerCategorySelectedLabelTarget
    labelEl.textContent = this._computeCategorySummary(labelEl)
  }

  _computeCategorySummary(labelEl) {
    const selected = this.categoryChipTargets.filter(c => c.dataset.active === "true")
    const allText = labelEl.dataset.allText || "All"
    const suffix = labelEl.dataset.multiSuffix || "selected"

    if (selected.length === 0) return allText
    if (selected.length === 1) return selected[0].dataset.label || selected[0].dataset.value
    return `${selected.length} ${suffix}`
  }

  _styleSegmentedChip(chip, active) {
    const activeClasses = ["bg-base-200", "shadow-sm", "text-base-content"]
    const inactiveClass = "text-base-content/50"
    if (active) {
      chip.classList.remove(inactiveClass)
      chip.classList.add(...activeClasses)
    } else {
      chip.classList.remove(...activeClasses)
      chip.classList.add(inactiveClass)
    }
  }

  _toggleClearBtn() {
    if (!this.hasClearSearchBtnTarget) return
    const hasText = this.searchInputTarget.value.trim().length > 0
    this.clearSearchBtnTarget.classList.toggle("hidden", !hasText)
  }
}
